import { ByWeekday, Options, RRule } from 'rrule';
import { RECURRENCE_CARD_COUNT } from '@shared/styles';
import moment from '@shared/util/moment';
import { getTimezone } from '@shared/util/timezone';
import { toRRuleDate } from './utils';

// Limit to 10 recurrences so that we don't repeat this indefinitely...
export function every2min() {
  return new RRule({
    freq: RRule.MINUTELY,
    interval: 2,
    count: 10,
    dtstart: toRRuleDate(moment().startOf('minute').toDate()),
    tzid: getTimezone(),
  });
}
export function every15min() {
  return new RRule({
    freq: RRule.MINUTELY,
    interval: 15,
    count: 10,
    dtstart: toRRuleDate(moment().startOf('minute').toDate()),
    tzid: getTimezone(),
  });
}

function ruleWithStartAtMidnightOfNextRecurrence(options: Partial<Options>): RRule {
  const ruleStartingTodayAtMidnight = new RRule({
    dtstart: moment().startOf('day').toDate(),
    tzid: getTimezone(),
    ...options,
  });
  const todayAtMidnight = moment().startOf('day').toDate();

  const nextRecurrence = ruleStartingTodayAtMidnight.after(todayAtMidnight, true);
  return new RRule({
    ...options,
    dtstart: nextRecurrence,
    tzid: getTimezone(),
    count: RECURRENCE_CARD_COUNT,
  });
}

export function daily() {
  return ruleWithStartAtMidnightOfNextRecurrence({ freq: RRule.DAILY });
}

export function mondays() {
  return ruleWithStartAtMidnightOfNextRecurrence({ freq: RRule.WEEKLY, byweekday: [RRule.MO] });
}
export function tuesdays() {
  return ruleWithStartAtMidnightOfNextRecurrence({ freq: RRule.WEEKLY, byweekday: [RRule.TU] });
}
export function wednesdays() {
  return ruleWithStartAtMidnightOfNextRecurrence({ freq: RRule.WEEKLY, byweekday: [RRule.WE] });
}
export function thursdays() {
  return ruleWithStartAtMidnightOfNextRecurrence({ freq: RRule.WEEKLY, byweekday: [RRule.TH] });
}
export function fridays() {
  return ruleWithStartAtMidnightOfNextRecurrence({ freq: RRule.WEEKLY, byweekday: [RRule.FR] });
}
export function saturdays() {
  return ruleWithStartAtMidnightOfNextRecurrence({ freq: RRule.WEEKLY, byweekday: [RRule.SA] });
}
export function sundays() {
  return ruleWithStartAtMidnightOfNextRecurrence({ freq: RRule.WEEKLY, byweekday: [RRule.SU] });
}

export function weekdays() {
  return ruleWithStartAtMidnightOfNextRecurrence({
    freq: RRule.WEEKLY,
    byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR],
  });
}
export function weekends() {
  return ruleWithStartAtMidnightOfNextRecurrence({
    freq: RRule.WEEKLY,
    byweekday: [RRule.SA, RRule.SU],
  });
}

export function days(interval: number = 1) {
  return ruleWithStartAtMidnightOfNextRecurrence({ freq: RRule.DAILY, interval: interval });
}

export function weekly(dayList: ByWeekday[], interval: number = 1) {
  return ruleWithStartAtMidnightOfNextRecurrence({
    freq: RRule.WEEKLY,
    byweekday: dayList,
    interval,
  });
}

export function monthly(bymonthday?: number, interval: number = 1) {
  return ruleWithStartAtMidnightOfNextRecurrence({
    freq: RRule.MONTHLY,
    bymonthday: bymonthday,
    interval,
  });
}

export function yearly(bymonthday?: number, bymonth?: number, interval: number = 1) {
  return ruleWithStartAtMidnightOfNextRecurrence({
    freq: RRule.YEARLY,
    bymonthday: bymonthday,
    bymonth: bymonth,
    interval,
  });
}
